import { FormEvent, useState } from 'react';
import {
  createReconstruction,
  getStoredSessionToken,
} from '../api/catalog';
import type { CatalogItemSummary, ReconstructionSummary } from '../types';
import { TopicInputList, validateTopics } from './TopicInputList';

interface CreateReconstructionModalProps {
  catalogItems: CatalogItemSummary[];
  onClose: () => void;
  onCreated: (reconstruction: ReconstructionSummary) => void;
}

const NEW_EXAM_VALUE = '__new__';

/**
 * Modal that lets any user start an exam reconstruction from the catalog.
 *
 * The user picks an exam from the catalog list (or types a new one) and the
 * exam date. Creation is public; an existing session is sent only so the server
 * can associate the reconstruction with its creator when possible.
 */
export function CreateReconstructionModal({
  catalogItems,
  onClose,
  onCreated,
}: CreateReconstructionModalProps) {
  const [selection, setSelection] = useState<string>(
    catalogItems[0]?.slug ?? NEW_EXAM_VALUE
  );
  const [newExamName, setNewExamName] = useState('');
  const [examDate, setExamDate] = useState('');
  const [topics, setTopics] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const isNewExam = selection === NEW_EXAM_VALUE || catalogItems.length === 0;

  function resolveExam(): { title: string; examSource?: string } | null {
    if (isNewExam) {
      const name = newExamName.trim();
      if (!name) return null;
      return { title: name };
    }
    const item = catalogItems.find((i) => i.slug === selection);
    if (!item) return null;
    return { title: item.name, examSource: item.slug };
  }

  async function doCreate() {
    const exam = resolveExam();
    if (!exam) {
      setError('בחרו מבחן או הזינו שם מבחן חדש.');
      return;
    }
    if (!examDate) {
      setError('בחרו את תאריך המבחן.');
      return;
    }
    const topicResult = validateTopics(topics);
    if (topicResult.error) {
      setError(topicResult.error);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const reconstruction = await createReconstruction(getStoredSessionToken(), {
        title: exam.title,
        exam_source: exam.examSource,
        exam_date: examDate,
        topics: topicResult.topics,
      });
      onCreated(reconstruction);
    } catch (err) {
      console.error('Failed to create reconstruction:', err);
      setError('יצירת השחזור נכשלה. נסו שוב.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    const exam = resolveExam();
    if (!exam) {
      setError('בחרו מבחן או הזינו שם מבחן חדש.');
      return;
    }
    if (!examDate) {
      setError('בחרו את תאריך המבחן.');
      return;
    }
    await doCreate();
  }

  return (
    <div
      data-testid="create-reconstruction-modal"
      className="fixed inset-0 z-40 flex items-center justify-center bg-slate-950/40 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-reconstruction-heading"
        className="card max-h-[calc(100vh-2rem)] w-full max-w-lg overflow-y-auto p-4 sm:p-6"
        onClick={(e) => e.stopPropagation()}
        dir="rtl"
      >
        <div className="flex items-start justify-between">
          <h2 id="create-reconstruction-heading" className="text-xl font-bold text-slate-950">יצירת שחזור מבחן</h2>
          <button
            type="button"
            data-testid="create-reconstruction-close"
            onClick={onClose}
            className="text-slate-400 transition hover:text-slate-700"
            aria-label="סגירה"
          >
            ✕
          </button>
        </div>
        <p className="mt-2 text-sm text-slate-600">
          בחרו מבחן והזינו את תאריך הבחינה. לאחר היצירה תקבלו קישור ציבורי לשיתוף —
          מילוי השחזור אינו דורש התחברות.
        </p>

        <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
          <div>
            <label className="block text-sm font-medium text-slate-700">מבחן</label>
            <select
              data-testid="reconstruction-exam-select"
              value={selection}
              onChange={(e) => setSelection(e.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
            >
              {catalogItems.map((item) => (
                <option key={item.slug} value={item.slug}>
                  {item.name}
                </option>
              ))}
              <option value={NEW_EXAM_VALUE}>אחר / מבחן חדש…</option>
            </select>
          </div>

          {isNewExam && (
            <div>
              <label className="block text-sm font-medium text-slate-700">שם המבחן</label>
              <input
                data-testid="reconstruction-new-exam-name"
                type="text"
                value={newExamName}
                onChange={(e) => setNewExamName(e.target.value)}
                placeholder="לדוגמה: קלינאות תקשורת 2026"
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                dir="auto"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700">תאריך המבחן</label>
            <input
              data-testid="reconstruction-exam-date"
              type="date"
              value={examDate}
              onChange={(e) => setExamDate(e.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
            />
          </div>

          <TopicInputList topics={topics} onChange={setTopics} />

          {error && (
            <p data-testid="reconstruction-error" className="text-sm text-red-600">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100"
            >
              ביטול
            </button>
            <button
              type="submit"
              data-testid="reconstruction-create-submit"
              disabled={submitting}
              className="btn-primary disabled:opacity-50"
            >
              {submitting ? 'יוצר…' : 'צור שחזור'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
